import { useInfiniteQuery } from '@tanstack/react-query'
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll'
import { useCallback, useMemo } from 'react'
import { DEFAULT_POLLING_INTERVAL_MS } from '@/consts/polling.consts'
import { useNewItemsDetection } from '@/hooks/useNewItemsDetection'
import { useAuth } from '@/contexts/AuthContext'
import { useActiveLocationsStore } from '@/stores/useActiveLocationsStore'
import { useStableArrayByIds } from '@/hooks/useStableArrayByIds'

interface UseInfiniteQueryWithScrollOptions<T> {
  queryKey: string[]
  queryFn: (params: {
    pageSize?: number
    startAfterId?: string
    filters: Record<string, string>
    sortBy: string | null
    sortDirection: 'asc' | 'desc'
  }) => Promise<{ items: T[]; hasMore: boolean; lastId?: string }>
  filters: Record<string, string>
  sortBy: string | null
  sortDirection: 'asc' | 'desc'
  pageSize?: number
  enablePolling?: boolean
  refetchInterval?: number
}

export function useInfiniteQueryWithScroll<T extends { [key: string]: any }>({
  queryKey,
  queryFn,
  filters,
  sortBy,
  sortDirection,
  pageSize = 30,
  enablePolling = false,
  refetchInterval = DEFAULT_POLLING_INTERVAL_MS,
}: UseInfiniteQueryWithScrollOptions<T>) {
  const { user } = useAuth()
  
  // Get location IDs key to reset new items detection on location change
  const locationIdsKey = useActiveLocationsStore(
    (state) => [...state.activeLocationIds].sort().join(',')
  )

  const query = useInfiniteQuery({
    queryKey: [...queryKey, filters, sortBy, sortDirection],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      queryFn({
        pageSize,
        startAfterId: pageParam,
        filters,
        sortBy,
        sortDirection,
      }),
    getNextPageParam: (lastPage: { items: T[]; lastDocumentId?: string }) =>
      lastPage.items?.length === pageSize ? lastPage.lastDocumentId : undefined,
    initialPageParam: undefined as string | undefined,
    refetchOnWindowFocus: false,
    refetchInterval: enablePolling && user ? refetchInterval : false,
    refetchIntervalInBackground: false,
  })

  const getItemId = useCallback(
    (item: T) => item.mezzaOrderId || item.id || item._id || JSON.stringify(item),
    []
  )

  // Flatten pages into items array with stable reference (prevents re-renders when data unchanged)
  const flattenedItems = useMemo(
    () => query.data?.pages.flatMap((page: { items: T[] }) => page.items),
    [query.data?.pages]
  )
  
  // Get stable array reference - only changes when item IDs actually change
  const items = useStableArrayByIds(flattenedItems, getItemId)

  const { newItemsCount, dismiss: dismissNewItems } = useNewItemsDetection({
    items: query.data?.pages[0]?.items,
    enabled: enablePolling && !query.isFetching,
    getItemId,
    resetDependency: locationIdsKey,
  })

  const { handleItemsRendered } = useInfiniteScroll({
    isLoading: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    totalItems: items.length,
    onLoadMore: () => query.fetchNextPage(),
  })

  return {
    ...query,
    handleItemsRendered,
    items,
    isLoading: query.isLoading || query.isFetchingNextPage,
    error: query.error?.message ?? null,
    hasMore: query.hasNextPage ?? false,
    newItemsCount,
    dismissNewItems,
  }
} 